import { useGetSpotWsStatusQuery, useGetSpotUserDataEventsQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * SpotInfraCard — health of the two persistent Spot WebSocket connections:
 *   1. !miniTicker@arr (price cache for hot paths)
 *   2. User Data Stream (real-time fills + balance deltas)
 *
 * Plus the last few executionReport events so the operator can see fills
 * landing as they happen.
 */
export function SpotInfraCard() {
  const status = useGetSpotWsStatusQuery(undefined, { pollingInterval: 30000 });
  const events = useGetSpotUserDataEventsQuery(
    { limit: 10, event_type: "executionReport" },
    { pollingInterval: 15000 }
  );

  const m: any = status.data?.market_ws || {};
  const u: any = status.data?.user_data || {};
  const rows: any[] = events.data?.events || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span>Spot WS infrastructure</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-border rounded p-2">
            <div className="flex items-center gap-2 mb-1">
              <Badge tone={m.thread_alive ? "good" : "bad"}>
                {m.thread_alive ? "● miniTicker" : "✕ miniTicker"}
              </Badge>
              <span className="text-muted">{m.cache_size ?? 0} symbols cached</span>
            </div>
            <div className="text-xs text-muted">
              reconnects: {m.reconnects ?? 0}
              {m.last_message_at && (
                <> · last msg {(m.last_message_at || "").slice(11, 19)}</>
              )}
            </div>
          </div>
          <div className="border border-border rounded p-2">
            <div className="flex items-center gap-2 mb-1">
              <Badge tone={u.thread_alive ? "good" : "bad"}>
                {u.thread_alive ? "● user-data" : "✕ user-data"}
              </Badge>
              <span className="text-muted">{u.event_count ?? 0} events</span>
            </div>
            <div className="text-xs text-muted">
              reconnects: {u.reconnects ?? 0}
              {u.last_event_at && (
                <> · last {u.last_event_type || "?"} at {(u.last_event_at || "").slice(11, 19)}</>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="font-semibold text-xs text-muted mb-1">Recent fills</div>
          {rows.length === 0 ? (
            <div className="text-xs text-muted">No executionReport events yet.</div>
          ) : (
            <div className="border border-border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-bg2 text-left text-muted">
                  <tr>
                    <th className="py-1 px-2">Time</th>
                    <th className="py-1 px-2">Symbol</th>
                    <th className="py-1 px-2">Side</th>
                    <th className="py-1 px-2 text-right">Filled qty</th>
                    <th className="py-1 px-2 text-right">Quote $</th>
                    <th className="py-1 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: any) => (
                    <tr key={r.id} className="border-t border-border/40">
                      <td className="py-1 px-2 text-muted">{(r.ts || "").slice(11, 19)}</td>
                      <td className="py-1 px-2 font-mono">{r.symbol}</td>
                      <td className="py-1 px-2">
                        <Badge tone={r.side === "BUY" ? "good" : "bad"}>{r.side}</Badge>
                      </td>
                      <td className="py-1 px-2 text-right font-mono">{Number(r.filled_qty || 0).toFixed(6)}</td>
                      <td className="py-1 px-2 text-right font-mono">{Number(r.quote_qty || 0).toFixed(2)}</td>
                      <td className="py-1 px-2 text-xs">{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
